export type WooPluginSlug = 'official-gateway' | 'wc-gateway' | 'wc-checkout'

const WOO_PLUGIN_SLUGS: WooPluginSlug[] = ['official-gateway', 'wc-gateway', 'wc-checkout']

export interface InstalledPluginVersion {
    slug: WooPluginSlug
    version: string
}

/** Parses the `slug/version` string the backend records on `integration.metadata.pluginVersion`. */
export function parseInstalledPluginVersion(raw: unknown): InstalledPluginVersion | null {
    if (typeof raw !== 'string') return null
    const separatorIndex = raw.indexOf('/')
    if (separatorIndex === -1) return null
    const slug = raw.slice(0, separatorIndex)
    const version = raw.slice(separatorIndex + 1)
    if (!version || !WOO_PLUGIN_SLUGS.includes(slug as WooPluginSlug)) return null
    return { slug: slug as WooPluginSlug, version }
}

export type PluginVersionState =
    | 'loading'
    /** No version yet, and the integration is recent enough to have downloaded a versioned build. */
    | 'never-seen'
    /** No version ever, on an integration older than the version header: a build from before it. */
    | 'never-reported'
    | 'up-to-date'
    | 'stale'
    | 'unknown-current'

export interface DerivePluginVersionStateInput {
    installed: InstalledPluginVersion | null
    current: { versions: Record<WooPluginSlug, string | null>; versionHeaderSince: string } | undefined
    isLoading: boolean
    integrationCreatedAt: Date | string | null | undefined
}

/**
 * Every build has declared its version since `versionHeaderSince`. An integration older than
 * that which has still never reported one is running a build from before — outdated, not
 * "not heard from yet". A newer integration downloaded a versioned build; it just has not sent
 * an order.
 */
export function derivePluginVersionState({
    installed,
    current,
    isLoading,
    integrationCreatedAt,
}: DerivePluginVersionStateInput): PluginVersionState {
    if (isLoading) return 'loading'

    if (!installed) {
        const predatesVersionHeader =
            !!integrationCreatedAt &&
            !!current?.versionHeaderSince &&
            new Date(integrationCreatedAt) < new Date(current.versionHeaderSince)
        return predatesVersionHeader ? 'never-reported' : 'never-seen'
    }

    const currentVersion = current?.versions[installed.slug]
    if (!currentVersion) return 'unknown-current'
    return currentVersion === installed.version ? 'up-to-date' : 'stale'
}
